/**
 * Client mirror of server/src/middleware/require-role.ts.
 *
 * Used to hide UI the current role cannot act on. The server enforces the same
 * map on every request — this is presentation only, never the security boundary.
 * Keep both files in sync.
 */

export const ROLES = {
  ADMIN: 'ADMIN',
  CONTRACTING: 'CONTRACTING',
  BROKER_SUPPORT: 'BROKER_SUPPORT',
  ONBOARDING: 'ONBOARDING',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

const ALL_STAFF: Role[] = [
  ROLES.ADMIN,
  ROLES.CONTRACTING,
  ROLES.BROKER_SUPPORT,
  ROLES.ONBOARDING,
];

export const PERMISSIONS = {
  'agents.read': ALL_STAFF,
  'agents.create': [ROLES.ADMIN, ROLES.ONBOARDING],
  'agents.edit': [ROLES.ADMIN, ROLES.CONTRACTING, ROLES.ONBOARDING],
  'agents.ssn.reveal': [ROLES.ADMIN, ROLES.CONTRACTING],
  'agents.financial.reveal': [ROLES.ADMIN, ROLES.CONTRACTING],
  'onboarding.validate': [ROLES.ADMIN, ROLES.CONTRACTING],
  'documents.read': ALL_STAFF,
  'documents.verify': [ROLES.ADMIN, ROLES.CONTRACTING, ROLES.ONBOARDING],
  'contracts.read': ALL_STAFF,
  'contracts.manage': [ROLES.ADMIN, ROLES.CONTRACTING],
  'carriers.read': ALL_STAFF,
  'carriers.manage': [ROLES.ADMIN, ROLES.CONTRACTING],
  'release.manage': [ROLES.ADMIN, ROLES.CONTRACTING, ROLES.BROKER_SUPPORT],
  'reports.read': [ROLES.ADMIN, ROLES.CONTRACTING],
  'campaigns.manage': [ROLES.ADMIN, ROLES.BROKER_SUPPORT, ROLES.ONBOARDING],
  'birthdays.manage': [ROLES.ADMIN],
  'notes.read': ALL_STAFF,
  'notes.write': ALL_STAFF,
  'notifications.read': ALL_STAFF,
} as const satisfies Record<string, readonly Role[]>;

export type Permission = keyof typeof PERMISSIONS;

export const ROLE_LABEL: Record<Role, string> = {
  ADMIN: 'Admin',
  CONTRACTING: 'Contracting',
  BROKER_SUPPORT: 'Broker Support',
  ONBOARDING: 'Onboarding',
};

export function rolesFromGroups(groups: string[] | undefined): Role[] {
  if (!groups) return [];
  return groups.filter((g): g is Role =>
    (Object.values(ROLES) as string[]).includes(g),
  );
}

export function hasPermission(
  roles: Role[],
  permission: Permission,
): boolean {
  const allowed = PERMISSIONS[permission] as readonly Role[];
  return roles.some((role) => allowed.includes(role));
}

/** ADMIN wins when a user carries several groups. */
export function primaryRole(roles: Role[]): Role | null {
  if (!roles.length) return null;
  if (roles.includes(ROLES.ADMIN)) return ROLES.ADMIN;
  return roles[0];
}
